/**
 * Balance math for the Executive monthly adjustment editor.
 *
 * Mirrors the validation in lambda/shared/projection-monthly-adjustments.ts (the backend is
 * the authority; this keeps the Save button honest before the round trip). Same codes, same
 * rule: the 12 values must sum exactly to the base spread's total.
 * frontend/src/utils/monthlyAdjustmentMath.test.ts shares its fixtures with the backend suite.
 */

import { MonthlyTotals, createEmptyMonthlyTotals } from '../types';

export const MONTH_KEYS = [
  'month1', 'month2', 'month3', 'month4', 'month5', 'month6',
  'month7', 'month8', 'month9', 'month10', 'month11', 'month12',
] as const;

/** Department -> metrics an Executive may adjust month by month (same table as the backend) */
export const ADJUSTABLE_METRICS: Record<string, readonly string[]> = {
  SERVICE: ['GROSS'],
  PARTS: ['GROSS'],
  BODY_SHOP: ['GROSS'],
  RENTAL: ['RENTAL_INCOME'],
  NEW_VEHICLE: ['GROSS', 'FI_INCOME', 'UNITS'],
  USED_VEHICLE: ['GROSS', 'FI_INCOME', 'UNITS'],
};


export type AdjustmentValidationCode =
  | 'NOT_ADJUSTABLE'
  | 'NOT_A_NUMBER'
  | 'NOT_AN_INTEGER'
  | 'NEGATIVE'
  | 'SUM_MISMATCH';

export interface AdjustmentValidationError {
  code: AdjustmentValidationCode;
  message: string;
  remaining?: number;
}

export function isAdjustableMetric(department: string, metric: string): boolean {
  return (ADJUSTABLE_METRICS[department] || []).includes(metric);
}

export function toMonthArray(m: MonthlyTotals | undefined | null): number[] {
  return MONTH_KEYS.map(k => Number(m?.[k]) || 0);
}

export function fromMonthArray(values: number[]): MonthlyTotals {
  const m = createEmptyMonthlyTotals();
  MONTH_KEYS.forEach((k, i) => { m[k] = values[i] ?? 0; });
  return m;
}

export function sumValues(values: number[]): number {
  return values.reduce((s, v) => s + (Number.isFinite(v) ? v : 0), 0);
}

/** target − allocated: positive means that much is still unallocated, negative means over */
export function remainingToAllocate(values: number[], targetTotal: number): number {
  return targetTotal - sumValues(values);
}

export function deltasFrom(values: number[], base: number[]): number[] {
  return values.map((v, i) => v - (base[i] ?? 0));
}

/**
 * Same rules and messages as the backend validateMonthlyValues. `targetTotal` is the sum of
 * the row's base (seasonality) spread, which can sit $1 from the annual by rounding; the 12
 * values must hit it exactly so the deltas sum to 0.
 */
export function validateDraft(
  department: string,
  metric: string,
  values: number[],
  targetTotal: number,
): AdjustmentValidationError | null {
  if (!isAdjustableMetric(department, metric)) {
    return { code: 'NOT_ADJUSTABLE', message: `${department} ${metric} cannot be adjusted month by month` };
  }
  for (let i = 0; i < 12; i++) {
    const v = values[i];
    const k = MONTH_KEYS[i];
    if (typeof v !== 'number' || !Number.isFinite(v)) return { code: 'NOT_A_NUMBER', message: `${k} must be a number` };
    if (!Number.isInteger(v)) return { code: 'NOT_AN_INTEGER', message: `${k} must be a whole number` };
    if (v < 0) return { code: 'NEGATIVE', message: `${k} cannot be negative` };
  }
  const remaining = remainingToAllocate(values, targetTotal);
  if (remaining !== 0) {
    const what = metric === 'UNITS' ? 'units' : 'dollars';
    return {
      code: 'SUM_MISMATCH',
      message: `The 12 months must add up to the annual total (${remaining > 0 ? `${remaining} ${what} left to allocate` : `${-remaining} ${what} over`})`,
      remaining,
    };
  }
  return null;
}
